(() => {
  if (customElements.get('ba-mini-cart-note')) return;
  const controllers = new WeakMap();

  class MiniCartNoteController {
    constructor(miniCart, form) {
      this.miniCart = miniCart;
      this.form = form;
      this.initialized = false;
      this.saved = '';
      this.desired = '';
      this.pending = null;
      this.error = false;
      this.touched = false;
      this.expanded = null;
      this.checkoutPending = false;
      this.form.addEventListener('submit', this.beforeCheckout.bind(this), true);
    }

    attach(component) {
      this.component = component;
      const serverNote = component.input.value;
      if (!this.initialized || (!this.pending && this.desired === this.saved && !this.error)) {
        this.saved = serverNote;
        this.desired = serverNote;
        this.initialized = true;
      }
      if (this.expanded === null) this.expanded = component.details.open;
      this.render();
    }

    edit(value) {
      this.desired = value;
      this.touched = true;
      this.error = false;
      clearTimeout(this.timer);
      this.render();
      this.timer = setTimeout(() => this.flush(), 650);
    }

    render() {
      const component = this.component;
      if (!component?.isConnected) return;
      if (component.input.value !== this.desired) component.input.value = this.desired;
      component.input.readOnly = this.checkoutPending;
      component.input.setAttribute('aria-invalid', this.error ? 'true' : 'false');
      component.details.open = this.expanded || this.error;
      component.retry.hidden = !this.error;
      component.status.textContent = this.error ? component.dataset.error
        : (this.pending || this.desired !== this.saved) ? component.dataset.saving
          : this.touched ? component.dataset.saved : '';
      component.toggleAttribute('data-note-error', this.error);
      if (this.pending) component.setAttribute('aria-busy', 'true');
      else component.removeAttribute('aria-busy');
    }

    async acquire() {
      const started = Date.now();
      while (this.form.isConnected) {
        if (typeof this.miniCart.beginMutation !== 'function') throw new Error('Cart unavailable');
        if (this.miniCart.beginMutation(this)) return;
        if (Date.now() - started > 15000) throw new Error('Cart busy');
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      throw new Error('Cart closed');
    }

    async persist() {
      while (this.desired !== this.saved) {
        await this.acquire();
        try {
          const value = this.desired;
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 12000);
          try {
            const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ note: value }),
              signal: controller.signal,
            });
            if (!response.ok) throw new Error('Note unavailable');
            const cart = await response.json();
            if (cart.note !== value && !(value === '' && cart.note === null)) throw new Error('Note unconfirmed');
            this.saved = value;
          } finally {
            clearTimeout(timeout);
          }
        } finally {
          this.miniCart.endMutation(this);
        }
      }
    }

    flush() {
      clearTimeout(this.timer);
      if (this.pending) return this.pending;
      if (this.desired === this.saved) {
        this.render();
        return Promise.resolve(true);
      }
      this.error = false;
      this.pending = this.persist()
        .then(() => true, () => {
          this.error = true;
          this.expanded = true;
          return false;
        })
        .finally(() => {
          this.pending = null;
          this.render();
        });
      this.render();
      return this.pending;
    }

    async beforeCheckout(event) {
      if (event.submitter?.name !== 'checkout') return;
      if (this.checkoutPending) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!this.pending && this.desired === this.saved && !this.error) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.checkoutPending = true;
      this.render();
      const saved = await this.flush();
      this.checkoutPending = false;
      this.render();
      if (!saved || !this.form.isConnected) {
        this.component?.input.focus({ preventScroll: true });
        return;
      }
      const button = this.form.querySelector('[name="checkout"]');
      if (button && !button.disabled) this.form.requestSubmit(button);
    }
  }

  customElements.define('ba-mini-cart-note', class extends HTMLElement {
    connectedCallback() {
      const miniCart = this.closest('mini-cart');
      const form = this.closest('form');
      this.input = this.querySelector('textarea[name="note"]');
      this.details = this.querySelector('details');
      this.status = this.querySelector('[role="status"]');
      this.retry = this.querySelector('button');
      if (!miniCart || !form || !this.input || !this.details || !this.status || !this.retry) return;
      if (!controllers.has(form)) {
        controllers.set(form, new MiniCartNoteController(miniCart, form));
      }
      this.controller = controllers.get(form);
      miniCart.baNoteController = this.controller;
      this.controller.attach(this);
      this.input.addEventListener('input', () => this.controller.edit(this.input.value));
      this.input.addEventListener('change', () => this.controller.flush());
      this.details.addEventListener('toggle', () => { this.controller.expanded = this.details.open; });
      this.retry.addEventListener('click', () => this.controller.flush());
    }
  });

  const MiniCart = customElements.get('mini-cart');
  if (MiniCart) {
    const updateQuantity = MiniCart.prototype.updateQuantity;
    MiniCart.prototype.updateQuantity = function (...args) {
      const pending = this.baNoteController?.pending;
      if (pending) return pending.then(() => updateQuantity.apply(this, args));
      return updateQuantity.apply(this, args);
    };
  }
})();
